import { useState, useMemo } from 'react';

import { Box, useTheme } from '@chakra-ui/react';
import {
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownPortal,
  DropdownSub,
  DropdownSubContent,
  DropdownSubTrigger,
  DropdownTrigger,
} from '@mesh.ai/rabbit';
import { useEmployees } from 'components/Views/Hooks/dataHooks';
import { checkStaticDataCompensationEnabled } from 'utils/helpers/featureFlags';

import { SelectDashed } from 'uiToolkitV1/Select';

import { CascadingOptionsMenu } from './CascadingOptionsMenu';
import { EmployeeSelector } from './EmployeeSelector';
import { MultiStepMenuSingleValue } from './MultiStepMenuSingleValue';
import { CascadingApplyOption, getDefaultCascadingOption } from './shared/cascadingOptions';
import { MOCK_ROWS_STAKEHOLDER_FLAT_LIST } from './shared/stakeholderConstants';

export const MOCKED_EMPLOYEE_OPTIONS_COMPENSATION = MOCK_ROWS_STAKEHOLDER_FLAT_LIST.map((row) => ({
  label: row.employee.displayName,
  subTitle: row.employee.email,
  profilePhoto: null,
  id: row.employee.id,
}));

export interface EmployeeOption {
  label: string;
  subTitle: string;
  profilePhoto: string;
  id: number;
}

interface SelectedEmployee {
  label: string;
  value: string;
  profilePhoto?: string;
}

interface StakeholderRow {
  NOTE_ID: string | number;
  selectedEmployee?: SelectedEmployee | null;
  [columnId: string]: unknown;
}

interface OptionConfig {
  value: string;
  label: string;
  isSubMenu?: boolean;
}

type MultiSelectMenuProps = {
  data: StakeholderRow;
  colDef: {
    columnId: string;
  };
  options: {
    [key: string]: OptionConfig;
  };
  onChange: (data: { value: string; employees?: EmployeeOption[]; applyToAllLevels?: boolean }) => void;
  isCascadingAllowed?: boolean;
};

const MultiSelectMenu = ({ data, colDef, options, onChange, isCascadingAllowed = false }: MultiSelectMenuProps) => {
  const { columnId } = colDef ?? {};
  const theme = useTheme();

  const selectedValue = data[columnId] as string;
  const [isAnyEmployeeDropdownOpen, setIsAnyEmployeeDropdownOpen] = useState(false);
  const [isStakeHolderDropdownOpen, setIsStakeHolderDropdownOpen] = useState(false);
  const [isManagerDropdownOpen, setIsManagerDropdownOpen] = useState(false);
  const [isSkipManagerDropdownOpen, setIsSkipManagerDropdownOpen] = useState(false);
  const [managerCascadingOption, setManagerCascadingOption] = useState<CascadingApplyOption>(
    getDefaultCascadingOption().value
  );
  const [skipManagerCascadingOption, setSkipManagerCascadingOption] = useState<CascadingApplyOption>(
    getDefaultCascadingOption().value
  );
  const { data: employees = [] } = useEmployees({ cohortId: -1 });

  const employeeOptions = useMemo(
    () =>
      checkStaticDataCompensationEnabled()
        ? MOCKED_EMPLOYEE_OPTIONS_COMPENSATION
        : employees.map((employee) => ({
            label: employee.displayName,
            subTitle: employee.email,
            profilePhoto: employee.profilePhoto,
            id: employee.id,
          })),
    [employees]
  );

  const closeParticipantsDropdown = () => {
    setIsStakeHolderDropdownOpen(false);
    setIsAnyEmployeeDropdownOpen(false);
    setIsManagerDropdownOpen(false);
    setIsSkipManagerDropdownOpen(false);
  };

  const onChangeOption = async ({
    value,
    selectedEmployees = [],
    applyToAllLevels = false,
  }: {
    value: string;
    selectedEmployees?: EmployeeOption[];
    applyToAllLevels?: boolean;
  }) => {
    onChange({ value, employees: selectedEmployees, applyToAllLevels });

    closeParticipantsDropdown();
  };

  const handleClickOutsideModal = () => {
    const participantSelectionModal = document.getElementById('participant-selection-modal');
    if (!participantSelectionModal) {
      setIsStakeHolderDropdownOpen(false);
    }
  };

  // Safe access to options and current value
  const currentSelectedOption = selectedValue && options[selectedValue] ? options[selectedValue] : null;

  // Update to handle multiple selected employees
  const selectedEmployees =
    selectedValue === options.ANY_EMPLOYEE?.value
      ? (data?.[`${columnId.toLowerCase()}SelectedEmployees`] as EmployeeOption[] | null) || []
      : [];

  const getDisplayValue = () => {
    if (selectedValue === options.ANY_EMPLOYEE?.value) {
      if (!checkStaticDataCompensationEnabled()) {
        return selectedEmployees.length > 0
          ? {
              label: `${selectedEmployees.length} employee(s) selected`,
              value: 'multiple',
              image: null,
              selectedEmployees,
            }
          : null;
      }
      return selectedEmployees.length > 0
        ? {
            label: `${selectedEmployees.length} employee(s) selected`,
            value: 'multiple',
            image: null,
            selectedEmployees,
          }
        : null;
    }
    return currentSelectedOption;
  };

  const displayValue = getDisplayValue();

  return (
    <Box display="flex" flexDirection="column" alignItems="flex-start">
      <Dropdown open={isStakeHolderDropdownOpen} onOpenChange={handleClickOutsideModal}>
        <Box
          sx={{
            'button[data-state="open"] .select__indicators svg': {
              color: theme.colors.button.blue.focus,
              transform: 'rotate(180deg)',
              transition: 'transform 0.2s',
            },
            "button[data-state='closed'] .select__indicators svg": {
              color: theme.colors.text.g1,
            },
            'button[data-state="open"] .select__control': {
              borderBottom: `1px solid ${theme.colors.button.blue.focus} !important`,
            },
          }}
          width="100%"
        >
          {' '}
          <DropdownTrigger
            style={{ width: '100%', textAlign: 'left' }}
            onClick={() => setIsStakeHolderDropdownOpen(true)}
          >
            <SelectDashed
              value={displayValue}
              components={
                selectedValue === options.ANY_EMPLOYEE?.value
                  ? {
                      SingleValue: MultiStepMenuSingleValue,
                    }
                  : {}
              }
              options={[]}
              handleChange={() => {}}
              placeholder=""
              getOptionValue={undefined}
              getOptionLabel={undefined}
              selectRef={undefined}
            />
          </DropdownTrigger>
        </Box>
        <DropdownPortal>
          <DropdownContent align="start">
            {Object.values(options).map((option) => {
              if (option.value === 'ANY_EMPLOYEE') {
                return (
                  <DropdownSub key={option.value}>
                    <DropdownSubTrigger
                      style={{ width: '100%' }}
                      onPointerEnter={(event) => {
                        if (event.pointerType === 'mouse') setIsAnyEmployeeDropdownOpen(true);
                      }}
                    >
                      {option.label}
                    </DropdownSubTrigger>
                    {isAnyEmployeeDropdownOpen && (
                      <DropdownPortal>
                        <DropdownSubContent
                          style={{ minWidth: '310px', padding: 0, overflow: 'hidden' }}
                          onPointerEnter={() => setIsAnyEmployeeDropdownOpen(true)}
                        >
                          <Box id="any-employee-selection-modal" width="100%">
                            <EmployeeSelector
                              options={employeeOptions}
                              handleSelection={(
                                selectedEmployeeOptions: EmployeeOption[],
                                applyToAllLevels: boolean
                              ) => {
                                if (options.ANY_EMPLOYEE) {
                                  onChangeOption({
                                    value: options.ANY_EMPLOYEE.value,
                                    selectedEmployees: selectedEmployeeOptions,
                                    applyToAllLevels,
                                  });
                                }
                                setIsAnyEmployeeDropdownOpen(false);
                                setIsStakeHolderDropdownOpen(false);
                              }}
                              selectedOptions={selectedEmployees}
                              isCascadingAllowed={isCascadingAllowed}
                            />
                          </Box>
                        </DropdownSubContent>
                      </DropdownPortal>
                    )}
                  </DropdownSub>
                );
              }

              // Handle MANAGER option
              if (option.value === 'MANAGER') {
                return (
                  <DropdownSub key={option.value}>
                    <DropdownSubTrigger
                      style={{ width: '100%' }}
                      onPointerEnter={(event) => {
                        if (event.pointerType === 'mouse') setIsManagerDropdownOpen(true);
                      }}
                    >
                      {option.label}
                    </DropdownSubTrigger>
                    {isManagerDropdownOpen && (
                      <DropdownPortal>
                        <DropdownSubContent
                          style={{ minWidth: '310px', padding: 0, overflow: 'hidden' }}
                          onPointerEnter={() => setIsManagerDropdownOpen(true)}
                        >
                          <Box id="manager-cascading-modal" width="100%">
                            <CascadingOptionsMenu
                              selectedOption={managerCascadingOption}
                              onOptionChange={(newOption) => {
                                setManagerCascadingOption(newOption);
                                onChangeOption({
                                  value: option.value,
                                  applyToAllLevels: newOption === CascadingApplyOption.ALL_LEVELS,
                                });
                                setIsManagerDropdownOpen(false);
                                setIsStakeHolderDropdownOpen(false);
                              }}
                            />
                          </Box>
                        </DropdownSubContent>
                      </DropdownPortal>
                    )}
                  </DropdownSub>
                );
              }

              // Handle SKIP_MANAGER option
              if (option.value === 'SKIP_MANAGER') {
                return (
                  <DropdownSub key={option.value}>
                    <DropdownSubTrigger
                      style={{ width: '100%' }}
                      onPointerEnter={(event) => {
                        if (event.pointerType === 'mouse') setIsSkipManagerDropdownOpen(true);
                      }}
                    >
                      {option.label}
                    </DropdownSubTrigger>
                    {isSkipManagerDropdownOpen && (
                      <DropdownPortal>
                        <DropdownSubContent
                          style={{ minWidth: '310px', padding: 0, overflow: 'hidden' }}
                          onPointerEnter={() => setIsSkipManagerDropdownOpen(true)}
                        >
                          <Box id="skip-manager-cascading-modal" width="100%">
                            <CascadingOptionsMenu
                              selectedOption={skipManagerCascadingOption}
                              onOptionChange={(newOption) => {
                                setSkipManagerCascadingOption(newOption);
                                onChangeOption({
                                  value: option.value,
                                  applyToAllLevels: newOption === CascadingApplyOption.ALL_LEVELS,
                                });
                                setIsSkipManagerDropdownOpen(false);
                                setIsStakeHolderDropdownOpen(false);
                              }}
                            />
                          </Box>
                        </DropdownSubContent>
                      </DropdownPortal>
                    )}
                  </DropdownSub>
                );
              }

              // Handle regular options
              return (
                <DropdownItem
                  key={option.value}
                  onClick={() => onChangeOption({ value: option.value, applyToAllLevels: isCascadingAllowed })}
                >
                  {option.label}
                </DropdownItem>
              );
            })}
          </DropdownContent>
        </DropdownPortal>
      </Dropdown>
    </Box>
  );
};

export default MultiSelectMenu;
